/**
 * KullaniciKart.jsx — Kullanıcılar sayfasının kart satırı (backlog #379)
 *
 * Kişi Listesi'ndeki PersonelKart'ın düzeni: solda vesikalık, ad (kullanıcı kartına giden bağlantı), kullanıcı adı,
 * telefon / e-posta / kimlik no, ortada firma + Yerli / Yabancı rozeti ve rol, sağda bağlı kişi ile son giriş.
 *   · Vesikalık çerçevesi = aktiflik. Fotoğraf kullanıcının değil, bağlı kişinin fotoğrafıdır (kullanıcıların kendi fotoğrafı yok,
 *     #378); bağlı kişi yoksa yer tutucu.
 *   · Kimlik no / pasaport no bağlı kişinindir (#490/#491, yalnızca page.kullanici): kullanıcıda saklanmaz, bağ kalkınca kaybolur.
 *   · Firma sütunu ilk firmayı + "+N" gösterir (bir kullanıcının tek firması olması beklenir, #308; eski kayıtlarda birden çok olabilir).
 *   · Bağlı kişi bağlantısı kişinin sayfasını (Kullanıcılar sekmesi) açar ve `geri` ile bu sayfaya döner (#262 / #273 deseni).
 *     Birleştirilmiş (gizli) bir kartsa kartta kalana gider.
 *   · "Son giriş" yalnızca BU sistemdeki girişlerdir (#378); hiç girmemişse yazı bunu söyler. "Fuar" üst çubuktaki seçili fuardır.
 *
 * WAS (#379): sol üst köşede taraf şeridi (Kök / Personel / Firma Tarafı / Rolsüz). #545'te kaldırıldı (bkz. kullaniciListe.js).
 */
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Badge, Button, Card, CardBody, Col, Row } from "reactstrap";
import { Building2, CalendarDays, Clock, Eye, Link2, Mail, Phone, Send } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PersonelAdBaglantisi } from "../Kisi/PersonelKartParcalari";
import { kisiSayfasiUrl } from "../Kisi/kisiBaglantilari";
import {
  KimlikNo, KullaniciAvatar, UyrukRozeti, adSoyad, fuarAdi, tarihSaat,
} from "./KullaniciParcalari";

/**
 * @param u        `GET /users/kullanici-listesi` satırı
 * @param href     kullanıcı kartının adresi (gerçek href: Ctrl / orta tık yeni sekmede açar)
 * @param onDetay  sol tık: sayfanın kendi gezinmesi (liste adresi + kaydırma konumu "geri" için bırakılır)
 */
export default function KullaniciKart({ u, href, onDetay, soluk = false }) {
  const { t } = useTranslation("components");
  const location = useLocation();
  const firmalar = u.firmalar || [];
  const ilkFirma = firmalar[0] || null;
  const fuar = u.seciliFuar || null;
  const kisi = u.kisi || null;
  const geri = `${location.pathname}${location.search}`;
  const kisiHedef = kisi ? (kisi.birlestirildigiPersonId ?? kisi.personId) : null;

  return (
    <Card className="mb-3 position-relative overflow-hidden" style={soluk ? { opacity: 0.5 } : undefined}>
      <CardBody>
        <Row className="align-items-center g-3">
          <Col md="auto">
            <div className="d-flex flex-column align-items-center gap-1">
              <KullaniciAvatar u={u} onClick={onDetay} aciklama={t("kullaniciKart.kullaniciKarti")} />
              <span className="text-muted font-monospace" style={{ fontSize: "0.7rem" }}>#{u.userId}</span>
            </div>
          </Col>

          <Col md={3}>
            <div className="fw-bold fs-5 text-break">
              <PersonelAdBaglantisi href={href} onGit={onDetay}>{adSoyad(u)}</PersonelAdBaglantisi>
            </div>
            <div className="text-muted small font-monospace text-break">{u.userLoginName}</div>
            {u.userTitle && <div className="text-muted small">{u.userTitle}</div>}
            <div className="text-muted d-flex flex-column" style={{ fontSize: "0.75rem" }}>
              <KimlikNo u={u} />
            </div>
            {u.userPhone && (
              <div className="text-muted d-flex align-items-center gap-1" style={{ fontSize: "0.75rem" }}>
                <Phone size={11} />{t("kullaniciKart.iletisim", { phone: u.userPhone })}
              </div>
            )}
            {u.userEmail && (
              <div className="text-muted d-flex align-items-center gap-1 text-break" style={{ fontSize: "0.75rem" }}>
                <Mail size={11} className="flex-shrink-0" />{u.userEmail}
              </div>
            )}
          </Col>

          <Col md={3}>
            {ilkFirma ? (
              <div className="d-flex align-items-start gap-1" title={firmalar.map((f) => f.firmaName).filter(Boolean).join(", ")}>
                <Building2 size={14} className="flex-shrink-0 text-muted mt-1" />
                <div>
                  <div className="fw-semibold text-break" style={{ fontSize: "0.95rem" }}>
                    {ilkFirma.firmaName || t("kullaniciKart.firmaNo", { id: ilkFirma.firmaId })}
                    {firmalar.length > 1 && <span className="text-muted fw-normal"> +{firmalar.length - 1}</span>}
                  </div>
                  <UyrukRozeti u={u} />
                </div>
              </div>
            ) : (
              <div>
                <div className="text-muted small">{t("kullaniciKart.firmaYok")}</div>
                <UyrukRozeti u={u} />
              </div>
            )}
            {u.userGroupName && (
              <div className="mt-2">
                <Badge color="light" className="text-body border fw-normal">{u.userGroupName}</Badge>
              </div>
            )}
          </Col>

          <Col md={2}>
            {kisi ? (
              <Link
                to={kisiSayfasiUrl({ personId: kisiHedef, tab: "kullanicilar", geri, geriEtiket: t("kullaniciKart.kullanicilaraDon") })}
                className="d-inline-flex align-items-start gap-1 text-decoration-none"
                title={t("kullaniciKart.bagliKisininSayfasiniAc")}
              >
                <Link2 size={14} className="flex-shrink-0 mt-1" />
                <span className="fw-semibold text-break">{kisi.adSoyad || t("kullaniciKart.kisiNo", { id: kisi.personId })}</span>
              </Link>
            ) : (
              <span className="text-muted small d-inline-flex align-items-center gap-1">
                <Link2 size={13} />{t("kullaniciKart.kisiyeBagliDegil")}
              </span>
            )}
          </Col>

          <Col>
            <div className="small d-flex align-items-center gap-1">
              <Clock size={13} className="text-muted flex-shrink-0" />
              {u.userLastLogin
                ? <span title={t("kullaniciKart.buSistemdekiSonGiris")}>{tarihSaat(u.userLastLogin)}</span>
                : <span className="text-muted">{t("kullaniciKart.hicGirisYapmadi")}</span>}
            </div>
            {fuar && (
              <div className="small d-flex align-items-center gap-1 text-break" title={fuar.fuarAdi || undefined}>
                <CalendarDays size={13} className="text-muted flex-shrink-0" />
                <span>{fuarAdi(fuar)}</span>
              </div>
            )}
            {/* #572: aktivasyon maili en son ne zaman gitti (hiç gitmediyse hiçbir şey çizilmez) */}
            {(u.aktivasyonMailiTarihi || u.aktivasyonMailiGonderildi) && (
              <div className="small d-flex align-items-center gap-1 text-muted" title={t("kullaniciKart.aktivasyonMailiBaslik")}>
                <Send size={13} className="flex-shrink-0" />
                <span>
                  {u.aktivasyonMailiTarihi
                    ? t("kullaniciKart.aktivasyonMaili", { tarih: tarihSaat(u.aktivasyonMailiTarihi) })
                    : t("kullaniciKart.aktivasyonMailiGitti")}
                </span>
              </div>
            )}
          </Col>

          <Col md="auto" className="d-flex align-items-center ms-auto mt-2 mt-md-0">
            <Button color="info" outline size="sm" onClick={onDetay}>
              <Eye size={14} className="me-1" />{t("kullaniciKart.detaylar")}
            </Button>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
}
